import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Development Dashboard - Feature Development Transparency Portal
 *
 * Admin tool for tracking feature development progress with real-time updates.
 * Provides transparency to stakeholders on what's being built and current status.
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiClient } from '../../utils/apiClient';
import { FaCode, FaCheckCircle, FaClock, FaPause, FaRocket, FaPlus, FaEdit, FaTrash, FaChartBar, FaEye, FaBullseye, FaTimes } from 'react-icons/fa';
import { PageContainer, PageTitle, Button, Text } from '../../components/PageStyles';
import { BaseModal } from '../../components/shared/BaseModal';
const STATUS_CONFIG = {
    'planned': { label: 'Planned', color: '#6b7280', icon: _jsx(FaClock, {}) },
    'in-progress': { label: 'In Progress', color: '#3b82f6', icon: _jsx(FaCode, {}) },
    'code-review': { label: 'Code Review', color: '#f59e0b', icon: _jsx(FaEdit, {}) },
    'testing': { label: 'Testing', color: '#8b5cf6', icon: _jsx(FaCheckCircle, {}) },
    'deployed': { label: 'Deployed', color: '#22c55e', icon: _jsx(FaRocket, {}) },
    'on-hold': { label: 'On Hold', color: '#ef4444', icon: _jsx(FaPause, {}) }
};
const PRIORITY_CONFIG = {
    'low': { label: 'Low', color: '#6b7280' },
    'medium': { label: 'Medium', color: '#3b82f6' },
    'high': { label: 'High', color: '#f59e0b' },
    'critical': { label: 'Critical', color: '#ef4444' }
};
const DevelopmentDashboard = () => {
    const [features, setFeatures] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [viewMode, setViewMode] = useState('cards');
    const [requestedFeatures, setRequestedFeatures] = useState([]);
    const [isCreating, setIsCreating] = useState(false);
    const [selectedFeatureForEdit, setSelectedFeatureForEdit] = useState(null);
    const [selectedFeatureForDetails, setSelectedFeatureForDetails] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    // Feature showcase feature list (import from showcase)
    const FEATURE_NAMES = {
        'price-calculator': 'Diamond Price Calculator',
        'appointment-scheduler': 'Virtual Consultation Booking',
        'custom-builder': 'Custom Diamond Builder',
        'comparison-tool': 'Enhanced Comparison Tool',
        'ai-recommendations': 'AI Diamond Matchmaker',
        'inventory-alerts': 'Real-Time Inventory Alerts',
        'cert-verification': 'Certification Verification',
        'market-trends': 'Market Trends Dashboard',
        'mobile-app': 'Mobile Companion App',
        'ar-try-on': 'AR Virtual Try-On',
        'crm-integration': 'Dealer CRM Integration',
        'invoice-system': 'Automated Invoicing',
        'shipping-tracker': 'Advanced Shipping Portal',
        'wishlist-sharing': 'Client Wishlist Sharing',
        'multilingual': 'Multi-Language Support',
        'live-chat': 'AI-Powered Live Chat'
    };
    // Create development entries for requested features
    const createDevelopmentEntries = async () => {
        if (requestedFeatures.length === 0)
            return;
        setIsCreating(true);
        const errors = [];
        try {
            // Create an entry for each requested feature
            for (const featureId of requestedFeatures) {
                try {
                    const response = await apiClient.post('feature-development', {
                        featureId,
                        featureName: FEATURE_NAMES[featureId] || featureId,
                        status: 'planned',
                        priority: 'medium',
                        assignedTo: 'Unassigned',
                        tasks: []
                    });
                    if (!response.ok) {
                        errors.push(`${FEATURE_NAMES[featureId]}: ${response.error || 'Failed to create'}`);
                    }
                }
                catch (err) {
                    errors.push(`${FEATURE_NAMES[featureId]}: ${err instanceof Error ? err.message : 'Error'}`);
                }
            }
            if (errors.length === 0) {
                // Success - clear requests and reload
                setRequestedFeatures([]);
                localStorage.removeItem('requested-features');
                await loadFeatures(); // Reload to show new entries
                alert(`Successfully created ${requestedFeatures.length} development entries!`);
            }
            else {
                alert(`Some entries failed:\n${errors.join('\n')}`);
            }
        }
        catch (error) {
            console.error('Error creating development entries:', error);
            alert('Failed to create development entries. Check console for details.');
        }
        finally {
            setIsCreating(false);
        }
    };
    // Load features from API
    const loadFeatures = async () => {
        try {
            setIsLoading(true);
            setError('');
            // TODO: Replace with actual API endpoint
            const response = await apiClient.get('feature-development');
            if (!response.ok || !response.data?.success) {
                throw new Error(response.error || 'Failed to load development data');
            }
            setFeatures(response.data.data);
        }
        catch (error) {
            console.error('Error loading development features:', error);
            // For now, use mock data if API not ready
            setFeatures(MOCK_FEATURES);
        }
        finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        loadFeatures();
        // Check for requested features from Feature Showcase
        const requested = localStorage.getItem('requested-features');
        if (requested) {
            try {
                setRequestedFeatures(JSON.parse(requested));
            }
            catch (e) {
                console.error('Error parsing requested features:', e);
            }
        }
    }, []);
    // Filter and sort features
    const filteredFeatures = (filterStatus === 'all'
        ? features
        : features.filter(f => f.status === filterStatus))
        .sort((a, b) => {
        // First, sort by deployment status (deployed goes last)
        if (a.status === 'deployed' && b.status !== 'deployed')
            return 1;
        if (a.status !== 'deployed' && b.status === 'deployed')
            return -1;
        // Within same deployment status, sort by progress descending (higher % first)
        if (b.progress !== a.progress) {
            return b.progress - a.progress;
        }
        // If same progress, sort by priority (critical > high > medium > low)
        const priorityOrder = { 'critical': 0, 'high': 1, 'medium': 2, 'low': 3 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
    const stats = {
        total: features.length,
        inProgress: features.filter(f => f.status === 'in-progress').length,
        completed: features.filter(f => f.status === 'deployed').length,
        avgProgress: Math.round(features.reduce((sum, f) => sum + f.progress, 0) / (features.length || 1))
    };
    const handleEditClick = (feature) => {
        setSelectedFeatureForEdit(feature);
        setEditFormData({
            status: feature.status,
            priority: feature.priority,
            assignedTo: feature.assignedTo,
            progress: feature.progress
        });
    };
    const handleDetailsClick = (feature) => {
        setSelectedFeatureForDetails(feature);
    };
    const handleSaveEdit = async () => {
        if (!selectedFeatureForEdit)
            return;
        try {
            const response = await apiClient.patch(`feature-development/${selectedFeatureForEdit.id}`, {
                ...editFormData,
                featureId: selectedFeatureForEdit.featureId,
                updateMessage: 'Updated via Development Dashboard',
                updateType: 'progress-update'
            });
            if (response.ok) {
                alert('Feature updated successfully!');
                setSelectedFeatureForEdit(null);
                await loadFeatures();
            }
            else {
                alert(`Failed to update: ${response.error}`);
            }
        }
        catch (error) {
            console.error('Error updating feature:', error);
            alert('Failed to update feature. Check console.');
        }
    };
    const handleDeleteFeature = async (feature) => {
        if (!confirm(`Are you sure you want to delete "${feature.featureName}" from tracking?\n\nThis will remove it from the Development Dashboard.`)) {
            return;
        }
        try {
            const response = await apiClient.delete(`feature-development/${feature.id}`);
            if (response.ok) {
                alert('Feature removed from tracking!');
                await loadFeatures();
            }
            else {
                alert(`Failed to delete: ${response.error}`);
            }
        }
        catch (error) {
            console.error('Error deleting feature:', error);
            alert('Failed to delete feature. Check console.');
        }
    };
    return (_jsxs(PageContainer, { children: [_jsx(PageTitle, { children: "Development Dashboard" }), _jsx(Text, { style: { marginBottom: '32px' }, children: "Transparent view of feature development progress and current workload" }), requestedFeatures.length > 0 && (_jsxs(RequestedBanner, { children: [_jsxs(BannerHeader, { children: [_jsxs(BannerTitle, { children: [_jsx(FaBullseye, { style: { marginRight: '8px' } }), "New Feature Requests from Stakeholders"] }), _jsx(BannerClose, { onClick: () => {
                                    setRequestedFeatures([]);
                                    localStorage.removeItem('requested-features');
                                }, children: _jsx(FaTimes, {}) })] }), _jsxs(BannerText, { children: [requestedFeatures.length, " feature", requestedFeatures.length > 1 ? 's' : '', " selected for development consideration:"] }), _jsx(RequestedList, { children: requestedFeatures.map(id => (_jsx(RequestedChip, { children: id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }, id))) }), _jsxs(BannerActions, { children: [_jsx(Button, { onClick: createDevelopmentEntries, disabled: isCreating, children: isCreating ? 'Creating...' : 'Create Development Entries' }), _jsx(Button, { variant: "secondary", onClick: () => {
                                    setRequestedFeatures([]);
                                    localStorage.removeItem('requested-features');
                                }, children: "Dismiss" })] })] })), _jsxs(StatsGrid, { children: [_jsxs(StatCard, { children: [_jsx(StatValue, { children: stats.total }), _jsx(StatLabel, { children: "Total Features" })] }), _jsxs(StatCard, { children: [_jsx(StatValue, { style: { color: '#3b82f6' }, children: stats.inProgress }), _jsx(StatLabel, { children: "In Progress" })] }), _jsxs(StatCard, { children: [_jsx(StatValue, { style: { color: '#22c55e' }, children: stats.completed }), _jsx(StatLabel, { children: "Deployed" })] }), _jsxs(StatCard, { children: [_jsxs(StatValue, { children: [stats.avgProgress, "%"] }), _jsx(StatLabel, { children: "Avg Progress" })] })] }), _jsxs(Controls, { children: [_jsxs(FilterGroup, { children: [_jsx(FilterLabel, { children: "Status Filter:" }), _jsxs(Select, { value: filterStatus, onChange: (e) => setFilterStatus(e.target.value), children: [_jsx("option", { value: "all", children: "All Features" }), Object.entries(STATUS_CONFIG).map(([key, config]) => (_jsx("option", { value: key, children: config.label }, key)))] })] }), _jsxs(ViewToggle, { children: [_jsx(ToggleButton, { "$active": viewMode === 'cards', onClick: () => setViewMode('cards'), children: "Card View" }), _jsx(ToggleButton, { "$active": viewMode === 'timeline', onClick: () => setViewMode('timeline'), children: "Timeline View" })] }), _jsxs(Button, { children: [_jsx(FaPlus, { style: { marginRight: '8px' } }), "Add Feature"] })] }), filterStatus !== 'all' && (_jsxs(ResultsCount, { children: ["Showing ", filteredFeatures.length, " ", filterStatus.replace('-', ' '), " feature", filteredFeatures.length !== 1 ? 's' : ''] })), viewMode === 'cards' ? (_jsx(FeatureGrid, { children: filteredFeatures.map(feature => (_jsxs(FeatureCard, { "$status": feature.status, children: [_jsxs(CardHeader, { children: [_jsxs(StatusBadge, { "$color": STATUS_CONFIG[feature.status].color, children: [STATUS_CONFIG[feature.status].icon, _jsx("span", { children: STATUS_CONFIG[feature.status].label })] }), _jsx(PriorityBadge, { "$color": PRIORITY_CONFIG[feature.priority].color, children: PRIORITY_CONFIG[feature.priority].label })] }), _jsx(FeatureTitle, { children: feature.featureName }), _jsxs(ProgressSection, { children: [_jsxs(ProgressHeader, { children: [_jsx(ProgressLabel, { children: "Progress" }), _jsxs(ProgressValue, { children: [feature.progress, "%"] })] }), _jsx(ProgressBar, { children: _jsx(ProgressFill, { "$width": feature.progress, "$color": STATUS_CONFIG[feature.status].color }) })] }), _jsxs(TaskList, { children: [_jsxs(TaskHeader, { children: ["Tasks (", feature.tasks.filter(t => t.completed).length, "/", feature.tasks.length, ")"] }), feature.tasks.slice(0, 3).map(task => (_jsxs(Task, { "$completed": task.completed, children: [task.completed ? _jsx(FaCheckCircle, {}) : _jsx("div", { className: "checkbox" }), _jsx("span", { children: task.description })] }, task.id))), feature.tasks.length > 3 && (_jsxs(TaskMore, { children: ["+", feature.tasks.length - 3, " more tasks"] }))] }), _jsx(MetaInfo, { children: _jsxs(MetaRow, { children: [_jsx(MetaLabel, { children: "Assigned:" }), _jsx(MetaValue, { children: feature.assignedTo })] }) }), _jsxs(CardActions, { children: [_jsxs(ActionButton, { onClick: (e) => {
                                        e.stopPropagation();
                                        handleEditClick(feature);
                                    }, children: [_jsx(FaEdit, {}), " Edit"] }), _jsxs(ActionButton, { onClick: (e) => {
                                        e.stopPropagation();
                                        handleDetailsClick(feature);
                                    }, children: [_jsx(FaEye, {}), " Details"] }), _jsxs(ActionButton, { onClick: (e) => {
                                        e.stopPropagation();
                                        handleDeleteFeature(feature);
                                    }, style: { color: '#ef4444' }, children: [_jsx(FaTrash, {}), " Delete"] })] })] }, feature.id))) })) : (_jsx(TimelineView, { children: filteredFeatures.map((feature, index) => (_jsxs(TimelineItem, { children: [_jsx(TimelineDot, { "$color": STATUS_CONFIG[feature.status].color }), _jsxs(TimelineContent, { children: [_jsxs(TimelineHeader, { children: [_jsx(TimelineTitle, { children: feature.featureName }), _jsxs(StatusBadge, { "$color": STATUS_CONFIG[feature.status].color, children: [STATUS_CONFIG[feature.status].icon, _jsx("span", { children: STATUS_CONFIG[feature.status].label })] })] }), _jsxs(TimelineMeta, { children: [feature.assignedTo, " \u2022 ", feature.progress, "%"] }), feature.updates.length > 0 && (_jsxs(LatestUpdate, { children: ["Latest: ", feature.updates[0].message, " - ", new Date(feature.updates[0].timestamp).toLocaleDateString()] }))] })] }, feature.id))) })), filteredFeatures.length === 0 && (_jsxs(EmptyState, { children: [_jsx(EmptyIcon, { children: _jsx(FaChartBar, {}) }), _jsx(EmptyText, { children: "No features match the selected filter" })] })), selectedFeatureForEdit && (_jsx(BaseModal, { isOpen: true, onClose: () => setSelectedFeatureForEdit(null), title: `Edit: ${selectedFeatureForEdit.featureName}`, footer: _jsxs(_Fragment, { children: [_jsx(Button, { onClick: handleSaveEdit, children: "Save Changes" }), _jsx(Button, { variant: "secondary", onClick: () => setSelectedFeatureForEdit(null), children: "Cancel" })] }), children: _jsxs(EditForm, { children: [_jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Status" }), _jsx(FormSelect, { value: editFormData.status || selectedFeatureForEdit.status, onChange: (e) => setEditFormData({ ...editFormData, status: e.target.value }), children: Object.entries(STATUS_CONFIG).map(([key, config]) => (_jsx("option", { value: key, children: config.label }, key))) })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Priority" }), _jsx(FormSelect, { value: editFormData.priority || selectedFeatureForEdit.priority, onChange: (e) => setEditFormData({ ...editFormData, priority: e.target.value }), children: Object.entries(PRIORITY_CONFIG).map(([key, config]) => (_jsx("option", { value: key, children: config.label }, key))) })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Assigned To" }), _jsx(FormInput, { type: "text", value: editFormData.assignedTo || selectedFeatureForEdit.assignedTo, onChange: (e) => setEditFormData({ ...editFormData, assignedTo: e.target.value }), placeholder: "Email or name" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Progress (%)" }), _jsx(FormInput, { type: "number", min: "0", max: "100", value: editFormData.progress ?? selectedFeatureForEdit.progress, onChange: (e) => setEditFormData({ ...editFormData, progress: parseInt(e.target.value) }) })] })] }) })), selectedFeatureForDetails && (_jsx(BaseModal, { isOpen: true, onClose: () => setSelectedFeatureForDetails(null), title: selectedFeatureForDetails.featureName, maxWidth: "900px", children: _jsxs(DetailsContent, { children: [_jsxs(DetailsSection, { children: [_jsx(DetailsSectionTitle, { children: "Status & Priority" }), _jsxs(DetailsGrid, { children: [_jsxs(DetailsItem, { children: [_jsx(DetailsLabel, { children: "Status:" }), _jsxs(StatusBadge, { "$color": STATUS_CONFIG[selectedFeatureForDetails.status].color, children: [STATUS_CONFIG[selectedFeatureForDetails.status].icon, _jsx("span", { children: STATUS_CONFIG[selectedFeatureForDetails.status].label })] })] }), _jsxs(DetailsItem, { children: [_jsx(DetailsLabel, { children: "Priority:" }), _jsx(PriorityBadge, { "$color": PRIORITY_CONFIG[selectedFeatureForDetails.priority].color, children: PRIORITY_CONFIG[selectedFeatureForDetails.priority].label })] }), _jsxs(DetailsItem, { children: [_jsx(DetailsLabel, { children: "Progress:" }), _jsxs(DetailsValue, { children: [selectedFeatureForDetails.progress, "%"] })] }), _jsxs(DetailsItem, { children: [_jsx(DetailsLabel, { children: "Assigned To:" }), _jsx(DetailsValue, { children: selectedFeatureForDetails.assignedTo })] })] })] }), _jsxs(DetailsSection, { children: [_jsx(DetailsSectionTitle, { children: "Timeline" }), _jsxs(DetailsGrid, { children: [_jsxs(DetailsItem, { children: [_jsx(DetailsLabel, { children: "Start Date:" }), _jsx(DetailsValue, { children: new Date(selectedFeatureForDetails.startDate).toLocaleDateString() })] }), selectedFeatureForDetails.actualCompletion && (_jsxs(DetailsItem, { children: [_jsx(DetailsLabel, { children: "Completed:" }), _jsx(DetailsValue, { children: new Date(selectedFeatureForDetails.actualCompletion).toLocaleDateString() })] }))] })] }), _jsxs(DetailsSection, { children: [_jsxs(DetailsSectionTitle, { children: ["Tasks (", selectedFeatureForDetails.tasks.filter(t => t.completed).length, "/", selectedFeatureForDetails.tasks.length, " completed)"] }), _jsxs(TaskList, { children: [selectedFeatureForDetails.tasks.map(task => (_jsxs(Task, { "$completed": task.completed, children: [task.completed ? _jsx(FaCheckCircle, {}) : _jsx("div", { className: "checkbox" }), _jsx("span", { children: task.description })] }, task.id))), selectedFeatureForDetails.tasks.length === 0 && (_jsx(EmptyTasksText, { children: "No tasks defined yet" }))] })] }), _jsxs(DetailsSection, { children: [_jsxs(DetailsSectionTitle, { children: ["Update History (", selectedFeatureForDetails.updates.length, ")"] }), _jsxs(UpdatesList, { children: [selectedFeatureForDetails.updates.slice().reverse().map((update, i) => (_jsxs(UpdateItem, { children: [_jsxs(UpdateHeader, { children: [_jsx(UpdateAuthor, { children: update.author }), _jsx(UpdateTime, { children: new Date(update.timestamp).toLocaleString() })] }), _jsx(UpdateMessage, { children: update.message })] }, i))), selectedFeatureForDetails.updates.length === 0 && (_jsx(EmptyTasksText, { children: "No updates yet" }))] })] })] }) }))] }));
};
// Mock data for development (remove when API ready)
const MOCK_FEATURES = [
    {
        id: '1',
        featureId: 'price-calculator',
        featureName: 'Diamond Price Calculator',
        status: 'in-progress',
        priority: 'high',
        assignedTo: 'Claude',
        progress: 65,
        startDate: '2025-10-13T00:00:00Z',
        actualCompletion: null,
        tasks: [
            { id: 't1', description: 'Design UI mockup', completed: true, completedAt: '2025-10-14T10:00:00Z' },
            { id: 't2', description: 'Implement price algorithm', completed: true, completedAt: '2025-10-15T14:00:00Z' },
            { id: 't3', description: 'Connect to pricing API', completed: false, completedAt: null },
            { id: 't4', description: 'Add wholesale/retail comparison', completed: false, completedAt: null },
            { id: 't5', description: 'Write tests and deploy', completed: false, completedAt: null }
        ],
        updates: [
            { timestamp: '2025-10-15T14:30:00Z', author: 'Claude', message: 'Completed price algorithm implementation', type: 'progress-update' },
            { timestamp: '2025-10-14T09:00:00Z', author: 'Claude', message: 'Started development', type: 'status-change' }
        ],
        metadata: {
            createdAt: '2025-10-13T00:00:00Z',
            updatedAt: '2025-10-15T14:30:00Z',
            createdBy: 'admin@royalgemsusa.com',
            lastUpdatedBy: 'Claude'
        }
    },
    {
        id: '2',
        featureId: 'appointment-scheduler',
        featureName: 'Virtual Consultation Booking',
        status: 'planned',
        priority: 'medium',
        assignedTo: 'Unassigned',
        progress: 0,
        startDate: '2025-10-25T00:00:00Z',
        actualCompletion: null,
        tasks: [
            { id: 't1', description: 'Research calendar APIs', completed: false, completedAt: null },
            { id: 't2', description: 'Design booking flow', completed: false, completedAt: null },
            { id: 't3', description: 'Implement timezone support', completed: false, completedAt: null }
        ],
        updates: [],
        metadata: {
            createdAt: '2025-10-13T00:00:00Z',
            updatedAt: '2025-10-13T00:00:00Z',
            createdBy: 'admin@royalgemsusa.com',
            lastUpdatedBy: 'admin@royalgemsusa.com'
        }
    }
];
// Theme breakpoints
const breakpoints = {
    small: '375px',
    mobile: '768px',
    tablet: '1024px',
};
// Styled Components
const RequestedBanner = styled.div `
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
  border: 2px solid #3b82f6;
  padding: 24px;
  margin-bottom: 32px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 20px 16px;
  }
`;
const BannerHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;
const BannerTitle = styled.h3 `
  font-family: 'Adamina', serif;
  font-size: 20px;
  color: var(--text-primary);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  display: flex;
  align-items: center;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 16px;
  }
`;
const BannerClose = styled.button `
  background: none;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 16px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  border-radius: 4px;

  &:hover {
    color: var(--text-primary);
    border-color: var(--accent-color);
    background: var(--bg-tertiary);
  }
`;
const BannerText = styled.p `
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
`;
const RequestedList = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;
const RequestedChip = styled.div `
  padding: 6px 12px;
  background: rgba(59, 130, 246, 0.2);
  border: 1px solid #3b82f6;
  color: #3b82f6;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
`;
const BannerActions = styled.div `
  display: flex;
  gap: 12px;

  @media (max-width: ${breakpoints.mobile}) {
    button {
      width: 100%;
    }
  }
`;
const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 48px;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;
const StatCard = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 24px;
  text-align: center;
`;
const StatValue = styled.div `
  font-size: 48px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 8px;
`;
const StatLabel = styled.div `
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
`;
const Controls = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  margin-bottom: 32px;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
  }
`;
const FilterGroup = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
`;
const FilterLabel = styled.label `
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
`;
const Select = styled.select `
  padding: 10px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--accent-color);
  }
`;
const ViewToggle = styled.div `
  display: flex;
  gap: 8px;
`;
const ToggleButton = styled.button `
  padding: 10px 20px;
  background: ${props => props.$active ? 'var(--accent-color)' : 'transparent'};
  color: ${props => props.$active ? 'var(--bg-primary)' : 'var(--text-secondary)'};
  border: 1px solid var(--border-color);
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: var(--accent-color);
  }
`;
const ResultsCount = styled.div `
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 24px;
  padding: 12px 16px;
  background: var(--bg-secondary);
  border-left: 4px solid var(--accent-color);
`;
const FeatureGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;
const FeatureCard = styled.div `
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  border-left: 4px solid ${props => STATUS_CONFIG[props.$status].color};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: ${props => STATUS_CONFIG[props.$status].color};
  }
`;
const CardHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;
const StatusBadge = styled.div `
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${props => `${props.$color}22`};
  border: 1px solid ${props => props.$color};
  color: ${props => props.$color};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;

  span {
    margin-left: 4px;
  }
`;
const PriorityBadge = styled.div `
  padding: 4px 10px;
  background: ${props => `${props.$color}22`};
  border: 1px solid ${props => props.$color};
  color: ${props => props.$color};
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
`;
const FeatureTitle = styled.h3 `
  font-family: 'Adamina', serif;
  font-size: 20px;
  font-weight: 400;
  color: var(--text-primary);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;
const ProgressSection = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const ProgressHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const ProgressLabel = styled.div `
  font-size: 12px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
`;
const ProgressValue = styled.div `
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
`;
const ProgressBar = styled.div `
  width: 100%;
  height: 8px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  overflow: hidden;
`;
const ProgressFill = styled.div `
  width: ${props => props.$width}%;
  height: 100%;
  background: ${props => props.$color};
  transition: width 0.5s ease;
`;
const TaskList = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const TaskHeader = styled.div `
  font-size: 12px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  margin-bottom: 4px;
`;
const Task = styled.div `
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: ${props => props.$completed ? 'var(--text-tertiary)' : 'var(--text-secondary)'};
  text-decoration: ${props => props.$completed ? 'line-through' : 'none'};

  svg {
    color: #22c55e;
    font-size: 14px;
  }

  .checkbox {
    width: 14px;
    height: 14px;
    border: 2px solid var(--border-color);
    border-radius: 3px;
  }

  span {
    flex: 1;
  }
`;
const TaskMore = styled.div `
  font-size: 12px;
  color: var(--text-tertiary);
  font-style: italic;
  padding-left: 24px;
`;
const MetaInfo = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
`;
const MetaRow = styled.div `
  display: flex;
  justify-content: space-between;
  font-size: 12px;
`;
const MetaLabel = styled.span `
  color: var(--text-tertiary);
  font-weight: 600;
`;
const MetaValue = styled.span `
  color: var(--text-primary);
`;
const CardActions = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;
const ActionButton = styled.button `
  flex: 1;
  padding: 10px;
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    border-color: var(--accent-color);
    color: var(--text-primary);
  }
`;
const TimelineView = styled.div `
  position: relative;
  padding-left: 40px;

  &::before {
    content: '';
    position: absolute;
    left: 15px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: var(--border-color);
  }
`;
const TimelineItem = styled.div `
  position: relative;
  margin-bottom: 32px;
`;
const TimelineDot = styled.div `
  position: absolute;
  left: -33px;
  top: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${props => props.$color};
  border: 3px solid var(--bg-primary);
  z-index: 1;
`;
const TimelineContent = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 20px;
`;
const TimelineHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 12px;
`;
const TimelineTitle = styled.h4 `
  font-family: 'Adamina', serif;
  font-size: 18px;
  color: var(--text-primary);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;
const TimelineMeta = styled.div `
  font-size: 13px;
  color: var(--text-tertiary);
  margin-bottom: 8px;
`;
const LatestUpdate = styled.div `
  font-size: 13px;
  color: var(--text-secondary);
  padding: 10px;
  background: var(--bg-tertiary);
  border-left: 3px solid var(--accent-color);
  font-style: italic;
`;
const EmptyState = styled.div `
  text-align: center;
  padding: 80px 24px;
  color: var(--text-tertiary);
`;
const EmptyIcon = styled.div `
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
`;
const EmptyText = styled.div `
  font-size: 16px;
  font-weight: 600;
`;
// Modal Components
const EditForm = styled.div `
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
const FormGroup = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const FormLabel = styled.label `
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
`;
const FormInput = styled.input `
  padding: 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--accent-color);
  }
`;
const FormSelect = styled.select `
  padding: 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  option {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
`;
const DetailsContent = styled.div `
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
const DetailsSection = styled.div `
  padding: 20px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
`;
const DetailsSectionTitle = styled.h4 `
  font-family: 'Adamina', serif;
  font-size: 16px;
  color: var(--text-primary);
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color);
`;
const DetailsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;
const DetailsItem = styled.div `
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const DetailsLabel = styled.span `
  font-size: 12px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
`;
const DetailsValue = styled.span `
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 600;
`;
const UpdatesList = styled.div `
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const UpdateItem = styled.div `
  padding: 12px;
  background: var(--bg-primary);
  border-left: 3px solid var(--accent-color);
`;
const UpdateHeader = styled.div `
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12px;
`;
const UpdateAuthor = styled.span `
  font-weight: 600;
  color: var(--text-primary);
`;
const UpdateTime = styled.span `
  color: var(--text-tertiary);
`;
const UpdateMessage = styled.div `
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
`;
const EmptyTasksText = styled.div `
  font-size: 13px;
  color: var(--text-tertiary);
  font-style: italic;
  text-align: center;
  padding: 20px;
`;
export default DevelopmentDashboard;
